import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Typography,
} from "@mui/material";
import { useBreakpoint } from "@avenue-8/sidekick-ui";
import { TableBox, TableText } from "@shared/components/Table";
import { TablePagination } from "@shared/components/TablePagination";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { contactsTableColumns } from "../contacts-helpers";
import type { ContactListItem } from "../contacts.types";
import { ContactsListActionsMenu } from "./ContactsListActionsMenu";
import { RadarMonitoringToggle } from "./RadarMonitoringToggle";
import { ContactsMobileList } from "./ContactsMobileList";

type ContactsListProps = {
  contacts: ContactListItem[];
  isLoading: boolean;
  onViewBriefing: (contactId: string) => void;
  onDeleteContact: (contactId: string, contactName: string) => void;
  pendingDeleteContactId: string | null;
  page: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  isMonitoredByContactId: Record<string, boolean>;
  isMonitoredSinceByContactId: Record<string, string | null>;
  pendingMonitoringContactIds: Set<string>;
  onToggleMonitoring: (
    contactId: string,
    contactName: string,
    nextValue: boolean,
  ) => void;
};

export const ContactsList = (props: ContactsListProps) => {
  const {
    contacts,
    isLoading,
    onViewBriefing,
    onDeleteContact,
    pendingDeleteContactId,
    page,
    totalPages,
    onChangePage,
    isMonitoredByContactId,
    isMonitoredSinceByContactId,
    pendingMonitoringContactIds,
    onToggleMonitoring,
  } = props;

  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const columns = hasRadarModule
    ? contactsTableColumns
    : contactsTableColumns.filter((column) => column.id !== "radarMonitoring");
  const isMobile = useBreakpoint("mobile");

  if (isMobile) {
    return (
      <TableBox>
        <ContactsMobileList
          contacts={contacts}
          isLoading={isLoading}
          onViewBriefing={onViewBriefing}
          onDeleteContact={onDeleteContact}
          pendingDeleteContactId={pendingDeleteContactId}
          hasRadarModule={hasRadarModule}
          isMonitoredByContactId={isMonitoredByContactId}
          isMonitoredSinceByContactId={isMonitoredSinceByContactId}
          pendingMonitoringContactIds={pendingMonitoringContactIds}
          onToggleMonitoring={onToggleMonitoring}
        />
        <TablePagination
          page={page}
          totalPages={totalPages}
          onChange={onChangePage}
        />
      </TableBox>
    );
  }

  return (
    <TableBox>
      <TableContainer>
        <Table aria-label="contacts table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={"align" in column ? column.align : "left"}
                  sx={{
                    minWidth:
                      "minWidth" in column ? column.minWidth : undefined,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              contacts.length === 0 &&
              Array(8)
                .fill(null)
                .map((_, index) => (
                  <TableRow hover tabIndex={-1} key={index}>
                    <TableCell colSpan={columns.length}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={48}
                      />
                    </TableCell>
                  </TableRow>
                ))}

            {!isLoading && contacts.length === 0 && (
              <TableRow hover>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" py={4}>
                    No contacts found
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              contacts.map((contact) => {
                const isDeleting = contact.id === pendingDeleteContactId;
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={contact.id}
                    onClick={() => !isDeleting && onViewBriefing(contact.id)}
                    sx={{
                      cursor: isDeleting ? "default" : "pointer",
                      opacity: isDeleting ? 0.5 : 1,
                      pointerEvents: isDeleting ? "none" : "auto",
                    }}
                  >
                    <TableCell>
                      <TableText>{contact.name}</TableText>
                    </TableCell>
                    <TableCell>
                      <TableText>{contact.email}</TableText>
                    </TableCell>
                    {hasRadarModule && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <RadarMonitoringToggle
                          isMonitored={
                            isMonitoredByContactId[contact.id] ?? false
                          }
                          isMonitoredSince={
                            isMonitoredSinceByContactId[contact.id] ?? null
                          }
                          isTogglePending={pendingMonitoringContactIds.has(
                            contact.id,
                          )}
                          onToggle={(nextValue) =>
                            onToggleMonitoring(
                              contact.id,
                              contact.name,
                              nextValue,
                            )
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ContactsListActionsMenu
                        onViewBriefingClick={() => onViewBriefing(contact.id)}
                        onDeleteClick={() =>
                          onDeleteContact(contact.id, contact.name)
                        }
                        isDeleteDisabled={pendingDeleteContactId !== null}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        page={page}
        totalPages={totalPages}
        onChange={onChangePage}
      />
    </TableBox>
  );
};
